import { useCallback, useRef, useState } from "react";
import { DataGrid } from "devextreme-react";
import {
  Column,
  ColumnChooser,
  Export,
  FilterRow,
  Grouping,
  GroupPanel,
  MasterDetail,
  Paging,
  SearchPanel,
  Sorting,
} from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { Popup } from "devextreme-react/popup";
import { Accordion, AccordionSummary, AccordionDetails, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { exportDataGrid } from "devextreme/excel_exporter";
import { Workbook } from "exceljs";
import { saveAs } from "file-saver";

import { PageProps } from "@ezhou/core";
import { MainCard } from "@ezhou/app_mantis_1_1";

import { JeExportByPostingDateModel } from "./model/jeExport";
import { ReviewJournalEntryExportSources } from "./ReviewJournalEntryExportSources";

type ReviewRawTransactionsFormProps = {
  pageProps: PageProps;
  jeExport: JeExportByPostingDateModel;
};

export const ReviewJournalEntryExports = (props: ReviewRawTransactionsFormProps) => {
  const gridData = props.jeExport.exports?.map((x) => {
    return { ...x.export, sources: x.sources };
  });
  const errors = props.jeExport.transactionErrors;

  const dataGridRef = useRef<DataGrid>(null);

  const applyIdFilter = useCallback((value: string) => {
    const grid = dataGridRef.current?.instance;
    if (!grid) {
      return;
    }

    const tokens = value
      .split(",")
      .map((token) => token.trim())
      .filter((token) => token.length > 0);
    if (tokens.length === 0) {
      grid.filter(null);
      return;
    }

    const selector = (rowData: { sources?: { transactionNo?: number; transactionSequenceNo?: number }[] }) =>
      (rowData.sources ?? [])
        .map(
          (source) =>
            `${source.transactionNo ?? ""} ${source.transactionSequenceNo ?? ""}`,
        )
        .join(" ");
    const conditions = tokens.map((token) => [selector, "contains", token]);
    if (conditions.length === 1) {
      grid.filter(conditions[0]);
      return;
    }
    const filterExpr: any[] = [];
    conditions.forEach((condition, index) => {
      if (index > 0) {
        filterExpr.push("or");
      }
      filterExpr.push(condition);
    });
    grid.filter(filterExpr);
  }, []);

  const handleToolbarPreparing = useCallback(
    (e: any) => {
      const items = e.toolbarOptions.items;
      const searchPanelIndex = items.findIndex(
        (item: any) => item.name === "searchPanel",
      );
      const idSearchItem = {
        location: "after",
        locateInMenu: "never",
        widget: "dxTextBox",
        options: {
          mode: "search",
          placeholder: "Transaction No / Sequence No (comma separated)",
          width: 300,
          showClearButton: true,
          valueChangeEvent: "keyup",
          onValueChanged: (args: { value?: string }) =>
            applyIdFilter(args.value ?? ""),
        },
      };
      if (searchPanelIndex >= 0) {
        items.splice(searchPanelIndex, 0, idSearchItem);
      } else {
        items.push(idSearchItem);
      }
    },
    [applyIdFilter],
  );

  const [popupVisible, setPopupVisible] = useState(false);
  const [popupTitle, setPopupTitle] = useState("");
  const [popupContent, setPopupContent] = useState("");

  const showPopup = useCallback((title: string, content: string) => {
    setPopupTitle(title);
    setPopupContent(content || "N/A");
    setPopupVisible(true);
  }, []);

  const hidePopup = useCallback(() => {
    setPopupVisible(false);
  }, []);

  const handleExporting = useCallback((e: any) => {
    const postingDate = props.jeExport.postingDate
      ? new Date(props.jeExport.postingDate)
      : undefined;
    const postingDateSuffix = postingDate
      ? `${postingDate.getFullYear()}-${String(postingDate.getMonth() + 1).padStart(2, "0")}-${String(postingDate.getDate()).padStart(2, "0")}`
      : "";
    const fileName = `JournalEntryExports${postingDateSuffix}.xlsx`;

    const workbook = new Workbook();
    const worksheet = workbook.addWorksheet("Journal Entry Exports");

    exportDataGrid({
      component: e.component,
      worksheet,
      autoFilterEnabled: true,
      selectedRowsOnly: e.selectedRowsOnly,
    }).then(() => {
      workbook.xlsx.writeBuffer().then((buffer) => {
        saveAs(
          new Blob([buffer as any], { type: "application/octet-stream" }),
          fileName,
        );
      });
    });

    e.cancel = true;
  }, [props.jeExport.postingDate]);

  const renderExceptionButton = useCallback((cellInfo: { data: { exception?: string } }) => {
    return (
      <Button
        text="View"
        type="default"
        stylingMode="text"
        disabled={!cellInfo.data.exception}
        onClick={() => showPopup("Exception", cellInfo.data.exception || "")}
      />
    );
  }, [showPopup]);

  const renderStackTraceButton = useCallback((cellInfo: { data: { stackTrace?: string } }) => {
    return (
      <Button
        text="View"
        type="default"
        stylingMode="text"
        disabled={!cellInfo.data.stackTrace}
        onClick={() => showPopup("Stack Trace", cellInfo.data.stackTrace || "")}
      />
    );
  }, [showPopup]);

  return (
    <>
      {errors && errors.length > 0 && (
        <Accordion defaultExpanded={false} sx={{ mb: 2 }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="transaction-errors-content"
            id="transaction-errors-header"
          >
            <Typography variant="h5">
              {props.pageProps.appUtil.getLocaleString("transactionErrors")} ({errors.length})
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <DataGrid
              dataSource={errors}
              height={300}
              showBorders={true}
              allowColumnResizing={true}
              columnResizingMode="widget"
              columnMinWidth={50}
              columnAutoWidth={true}
            >
              <Paging defaultPageSize={10} defaultPageIndex={0} />
              <Column dataField="message" caption="Error Message" />
              <Column caption="Exception Detail" width={150} alignment="center" cellRender={renderExceptionButton} />
              <Column caption="Stack Trace" width={120} alignment="center" cellRender={renderStackTraceButton} />
            </DataGrid>
          </AccordionDetails>
        </Accordion>
      )}
      <MainCard>
      <DataGrid
        ref={dataGridRef}
        dataSource={gridData}
        height={600}
        showBorders={true}
        allowColumnResizing={true}
        columnResizingMode="widget"
        columnMinWidth={50}
        columnAutoWidth={true}
        onExporting={handleExporting}
        onToolbarPreparing={handleToolbarPreparing}
      >
        {/* <Scrolling mode="infinite" /> */}
        <Paging defaultPageSize={10} defaultPageIndex={0} />
        <GroupPanel visible={true} />
        <Grouping autoExpandAll={false} />
        <Export enabled={true} allowExportSelectedData={true} />
        <SearchPanel
          visible={true}
          highlightCaseSensitive={true}
          placeholder={props.pageProps.appUtil.getLocaleString("search")}
        />
        <ColumnChooser enabled={true} />
        <Sorting mode="multiple" />
        <FilterRow visible={true} />
        {buildExportColumns(props.pageProps, props.jeExport.exports![0].export)}
        <MasterDetail enabled={true} component={ReviewJournalEntryExportSources} />
      </DataGrid>
      </MainCard>
      <Popup
        visible={popupVisible}
        onHiding={hidePopup}
        title={popupTitle}
        showCloseButton={true}
        width="60%"
        height="auto"
        maxHeight="80%"
      >
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", margin: 0 }}>
          {popupContent}
        </pre>
      </Popup>
    </>
  );
};

const buildExportColumns = (pageProps: PageProps, data: any) => {
  const keys: string[] = [];
  for (const key in data) {
    keys.push(key);
  }
  return keys.map((key: string, index: number) => {
    // DevExtreme treats a dot in `dataField` as a nested-property path, so field
    // names that contain a dot (e.g. "Account No.", "Post No.") resolve to an empty
    // cell. Read the value explicitly via calculateCellValue for those fields.
    if (key.includes(".")) {
      return (
        <Column
          key={index}
          name={key}
          caption={key}
          calculateCellValue={(rowData: any) => rowData[key]}
          calculateSortValue={(rowData: any) => rowData[key]}
          allowFiltering={true}
          allowSorting={true}
        ></Column>
      );
    }
    return <Column key={index} caption={key} dataField={key} allowFiltering={true} allowSorting={true}></Column>;
  });
};
